import React, { useState, useCallback, createContext, useMemo } from 'react';
import utils from 'utils';
import classNames from 'classnames';
import ResolveHeader from 'components/ResolveHeader';
import { Outlet, useLocation } from 'react-router-dom';
import { SmallFooter } from 'components/SmallFooter';
import FooterWrapper from 'components/FooterWrapper';

import './Dash.styles.scss';

type MainLayoutContextValue = {
  addDashClassName: (classes: string) => void;
  addMainClassName: (classes: string) => void;
  addFooterClassName: (classes: string) => void;
  setDashClassName: React.Dispatch<React.SetStateAction<string>>;
  setMainClassName: React.Dispatch<React.SetStateAction<string>>;
  setFooterClassName: React.Dispatch<React.SetStateAction<string>>;
  resetDashClassName: () => void;
  resetMainClassName: () => void;
  resetFooterClassName: () => void;
};

export const MainLayoutContext = createContext<MainLayoutContextValue>({
  addDashClassName: utils.noop(),
  addMainClassName: utils.noop(),
  addFooterClassName: utils.noop(),
  setDashClassName: utils.noop(),
  setMainClassName: utils.noop(),
  setFooterClassName: utils.noop(),
  resetDashClassName: utils.noop(),
  resetMainClassName: utils.noop(),
  resetFooterClassName: utils.noop(),
});

export const DASH_DEFAULT_CLASSES = 'dash';
const DASH_MAIN_DEFAULT_CLASSES = 'dash__main';
const DASH_FOOTER_DEFAULT_CLASSES = 'dash__footer';

const BUSINESS_ROUTES = 'business';
const URGENT_ROUTES = 'urgent';
const NOT_FOUND_ROUTE = 'page-not-found';

const PAGES_WITH_CLASSIC_FOOTER = [
  BUSINESS_ROUTES,
  URGENT_ROUTES,
  NOT_FOUND_ROUTE,
];

const Dash: React.FC = () => {
  const [dashClassName, setDashClassName] = useState(DASH_DEFAULT_CLASSES);
  const [mainClassName, setMainClassName] = useState(DASH_MAIN_DEFAULT_CLASSES);
  const [footerClassName, setFooterClassName] = useState(
    DASH_FOOTER_DEFAULT_CLASSES,
  );

  const { pathname } = useLocation();

  const showMainFooter = useMemo(
    () => PAGES_WITH_CLASSIC_FOOTER.some((page) => pathname.includes(page)),
    [pathname],
  );

  const addDashClassName = useCallback((classes) => {
    setDashClassName((old) => classNames(old, classes));
  }, []);

  const addMainClassName = useCallback((classes) => {
    setMainClassName((old) => classNames(old, classes));
  }, []);

  const addFooterClassName = useCallback((classes) => {
    setFooterClassName((old) => classNames(old, classes));
  }, []);

  const resetDashClassName = useCallback(() => {
    setDashClassName(DASH_DEFAULT_CLASSES);
  }, []);

  const resetMainClassName = useCallback(() => {
    setMainClassName(DASH_MAIN_DEFAULT_CLASSES);
  }, []);

  const resetFooterClassName = useCallback(() => {
    setFooterClassName(DASH_FOOTER_DEFAULT_CLASSES);
  }, []);

  return (
    <div className={classNames(dashClassName)}>
      <MainLayoutContext.Provider
        value={{
          addDashClassName,
          addMainClassName,
          addFooterClassName,
          setDashClassName,
          setMainClassName,
          setFooterClassName,
          resetDashClassName,
          resetMainClassName,
          resetFooterClassName,
        }}
      >
        <ResolveHeader />

        <main className={classNames(mainClassName)}>
          <Outlet />
        </main>

        {showMainFooter ? (
          <FooterWrapper className={footerClassName} />
        ) : (
          <SmallFooter className={footerClassName} />
        )}
      </MainLayoutContext.Provider>
    </div>
  );
};

export default Dash;
